import { c as _c } from "react/compiler-runtime";
import type { Icon } from '@onefootprint/icons';
import { cva } from 'class-variance-authority';
import Link from 'next/link';
import { forwardRef, memo } from 'react';
import TooltipWrapper from '../tooltip-wrapper/tooltip-wrapper';
export type NavLinkProps = {
  href: string;
  icon: Icon;
  selected: boolean;
  text: string;
  isCollapsed?: boolean;
  isNew?: boolean;
  annotationBadge?: React.ReactNode;
};
const navLinkStyles = cva('flex items-center no-underline rounded-default relative transition-transform duration-fast ease-out active:scale-pressed motion-reduce:active:scale-100', {
  variants: {
    selected: {
      true: 'text-primary bg-senary/70',
      false: 'text-tertiary hover:text-primary hover:bg-hover'
    },
    isCollapsed: {
      true: 'size-7 justify-center mx-auto',
      false: 'h-8 md:h-7 px-1.5 gap-2 w-full'
    }
  },
  compoundVariants: [{
    selected: true,
    isCollapsed: false,
    className: 'text-label-3'
  }, {
    selected: false,
    isCollapsed: false,
    className: 'text-body-3'
  }]
});
const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>((t0, ref) => {
  const $ = _c(19);
  const {
    href,
    icon: Icon,
    selected,
    text,
    isCollapsed,
    annotationBadge
  } = t0;
  let t1;
  if ($[0] !== isCollapsed || $[1] !== selected) {
    t1 = navLinkStyles({
      selected,
      isCollapsed
    });
    $[0] = isCollapsed;
    $[1] = selected;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== Icon) {
    t2 = <Icon />;
    $[3] = Icon;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== annotationBadge || $[6] !== isCollapsed || $[7] !== text) {
    t3 = !isCollapsed && <>{text}{annotationBadge}</>;
    $[5] = annotationBadge;
    $[6] = isCollapsed;
    $[7] = text;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== href || $[10] !== ref || $[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = <Link href={href} className={t1} ref={ref}>{t2}{t3}</Link>;
    $[9] = href;
    $[10] = ref;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const linkContent = t4;
  let t5;
  if ($[15] !== isCollapsed || $[16] !== linkContent || $[17] !== text) {
    t5 = isCollapsed ? <TooltipWrapper tooltipContent={text} disableHoverable={!isCollapsed}>{linkContent}</TooltipWrapper> : linkContent;
    $[15] = isCollapsed;
    $[16] = linkContent;
    $[17] = text;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
});
NavLink.displayName = 'NavLink';
export default memo(NavLink);